import type { APIResult } from "../models/APIResult";
//import type { Order, SyncChanges } from "../models/order";
import { saveOrder, getImageByServerId, saveDownloadedImage, markOrderForDeletion, saveSyncMetadata, getOrderById, getAssignedOrders, getSyncMetadata } from "./db";
import { getValidToken, handleAuthError, login as authLogin, type TokenData, getUserRole } from "./auth";
import { API_BASE } from "../config";
import type { Wizard5Drawings } from "../models/wizard5Drawings";
import type { ClientOrders } from "../models/clientOrders";
import type { Order } from "../models/order";
import type { ClientSyncResult } from "../models/clientSyncResult";
import type { ClientOptionsResponse } from "../models/clientOptionsResponse";

/**
 * Determine if an order should be marked for deletion based on status and user role
 * @param status The order status
 * @param userRole The user's role ('sales' or 'surveyor')
 * @returns true if order should be marked for deletion
 */
function shouldDeleteOrder(status: string, userRole: 'sales' | 'surveyor'): boolean {
  if (userRole === 'sales') {
    // Sales Rep: Delete Cancelled, Order
    const SALES_DELETE = ['Cancelled', 'Order'];
    return SALES_DELETE.includes(status);
  } else if (userRole === 'surveyor') {
    // Surveyor: Delete Cancelled, Complete
    const SURVEYOR_DELETE = ['Cancelled', 'Complete'];
    return SURVEYOR_DELETE.includes(status);
  }
  
  return false;
}

export async function login(username: string, password: string): Promise<TokenData> {
  return await authLogin(username, password);
}

export async function syncAssignedOrders() {
  // Get local order IDs from the database
  const localOrders = await getAssignedOrders();
  const orderIds = localOrders.map(order => order.id);
  
  // Get last sync metadata
  const syncMetadata = await getSyncMetadata();
  const lastSync = syncMetadata ? syncMetadata.last_server_time : null;
  
  //console.log(`[syncAssignedOrders] Sending ${orderIds.length} order IDs and last_sync: ${lastSync}`);
  
  // Prepare request body
  const requestBody = {
    order_ids: orderIds,
    last_sync: lastSync
  };
  
  const token = await getValidToken();
  
  let response = await fetch(`${API_BASE}/orders/assigned`, {
    method: 'POST',
    headers: { 
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(requestBody)
  });
  
  // Handle 401 - try token refresh and retry once
  if (response.status === 401) {
    const shouldRetry = await handleAuthError(response);
    if (shouldRetry) {
      const newToken = await getValidToken();
      response = await fetch(`${API_BASE}/orders/assigned`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${newToken}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });
    } else {
      throw new Error('Authentication failed. Please login again.');
    }
  }
  
  if (!response.ok) {
    throw new Error(`Failed to fetch assigned orders: ${response.statusText}`);
  }
  
  const data: APIResult<ClientOrders> = await response.json();
  //console.log("[syncAssignedOrders] Received data:", data);
  
  if (!data.success) {
    throw new Error(`Failed to sync orders: ${data.message}`);
  }
  
  // Get user role for deletion logic
  const userRole = getUserRole();
  if (!userRole) {
    throw new Error('Unable to determine user role from token');
  }
  
  //console.log(`[syncAssignedOrders] User role: ${userRole}`);
  
  // PHASE 1: Process full orders array (upsert)
  //console.log(`[syncAssignedOrders] Phase 1: Processing ${data.data.orders.length} full order(s)`);
  
  for (const order of data.data.orders) {
    //console.log(`[syncAssignedOrders] Upserting order ${order.id} with status: ${order.status}`);
    
    // Save/update order with all fields including sync_version and last_modified
    await saveOrder(order);
    
    // Sync images for this order
    try {
      // Parse step_data to get wizard step 5 data (photos)
      const step5Data = order.step_data?.steps?.step5?.data as Wizard5Drawings;
      
      if (step5Data && step5Data.photos && Array.isArray(step5Data.photos)) {
        //console.log(`[syncAssignedOrders] Order ${order.id} has ${step5Data.photos.length} photo(s) on server`);
        
        // For each server image ID, check if we have it locally
        for (const serverId of step5Data.photos) {
          if (!serverId) continue; // Skip empty IDs
          
          // Check if image already exists locally
          const existingImage = await getImageByServerId(serverId, order.id);
          
          if (!existingImage) {
            // Image doesn't exist locally - download it
            //console.log(`[syncAssignedOrders] Downloading image ${serverId} for order ${order.id}`);
            
            try {
              const imageData = await downloadImage(serverId);
              
              // Save downloaded image to local database
              const filename = `photo_${serverId}.jpg`;
              await saveDownloadedImage(order.id, imageData.step, serverId, imageData.blob, filename, imageData.tag);
              
              //console.log(`[syncAssignedOrders] Image ${serverId} downloaded and saved locally`);
            } catch (downloadError) {
              //console.error(`[syncAssignedOrders] Failed to download image ${serverId}:`, downloadError);
              // Continue with other images even if one fails
            }
          } else {
            //console.log(`[syncAssignedOrders] Image ${serverId} already exists locally`);
          }
        }
      }
    } catch (imageError) {
      console.error(`[syncAssignedOrders] Error syncing images for order ${order.id}:`, imageError);
      // Continue with other orders even if image sync fails
    }
  }
  
  // PHASE 2: Process sync_changes array
  if (data.data.sync_changes && data.data.sync_changes.length > 0) {
    //console.log(`[syncAssignedOrders] Phase 2: Processing ${data.data.sync_changes.length} sync change(s)`);
    
    for (const change of data.data.sync_changes) {
      //console.log(`[syncAssignedOrders] Processing change for order ${change.order_id}: status=${change.status}, sync_stamp=${change.sync_stamp}`);
      
      // Get the existing order
      const existingOrder = await getOrderById(change.order_id);
      
      if (existingOrder) {
        // Update status and last_modified
        existingOrder.status = change.status;
        existingOrder.last_modified = change.sync_stamp;
        
        // Check if order should be marked for deletion based on role
        if (shouldDeleteOrder(change.status, userRole)) {
          //console.log(`[syncAssignedOrders] Order ${change.order_id} status ${change.status} triggers deletion for ${userRole}`);
          await saveOrder(existingOrder);
          await markOrderForDeletion(change.order_id, change.sync_stamp);
        } else {
          // Just update the order without marking for deletion
          await saveOrder(existingOrder);
          //console.log(`[syncAssignedOrders] Order ${change.order_id} updated with new status: ${change.status}`);
        }
      } else {
        console.warn(`[syncAssignedOrders] Order ${change.order_id} not found locally for sync_change update`);
      }
    }
  } else {
    //console.log('[syncAssignedOrders] Phase 2: No sync_changes to process');
  }
  
  // PHASE 3: Save sync metadata
  if (data.data.server_time) {
    //console.log(`[syncAssignedOrders] Phase 3: Saving sync metadata with server_time: ${data.data.server_time}`);
    await saveSyncMetadata(data.data.server_time);
  }
  
  //console.log('[syncAssignedOrders] Sync completed successfully');
}



interface ImageRow {
  id: number;
  order_id: number;
  step: number;
  blob: Uint8Array;
  filename: string;
  synced_at: string | null;
  tag: string;
}

export async function uploadImage(image: ImageRow): Promise<string> {
  const token = await getValidToken();
  
  // Convert Uint8Array blob to File object for upload
  const blob = new Blob([image.blob as BlobPart]);
  const file = new File([blob], image.filename);
  
  const formData = new FormData();
  formData.append('file', file);
  formData.append('orderid', image.order_id.toString());
  formData.append('step', image.step.toString());
  formData.append('tag', image.tag);
  
  let response = await fetch(`${API_BASE}/orders/upload/photo`, {
    method: 'POST',
    headers: { 
      'Authorization': `Bearer ${token}`,
      //'Content-Type': 'application/json' 
    },
    credentials: 'include',
    body: formData,
  });
  
  // Handle 401 - try token refresh and retry once
  if (response.status === 401) {
    const shouldRetry = await handleAuthError(response);
    if (shouldRetry) {
      const newToken = await getValidToken();
      response = await fetch(`${API_BASE}/orders/upload/photo`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${newToken}`,
          //'Content-Type': 'application/json' 
        },
        credentials: 'include',
        body: formData,
      });
    } else {
      throw new Error('Authentication failed. Please login again.');
    }
  }
  
  if (!response.ok) {
    throw new Error(`Image upload failed: ${response.statusText}`);
  }
  
  // Extract server image ID from response
  const result: APIResult<string> = await response.json();
  if (!result.success || !result.data) {
    throw new Error('Failed to get server image ID from upload response');
  }
  
  return result.data;
}

// interface OrderRow {
//   id: number;
//   salesrep_id: number;
//   status: string;
//   step_data: string;
//   created_at: string;
//   updated_at: string;
//   synced_at: string | null;
// }

export async function syncOrder(order: Order): Promise<any> {
  const token = await getValidToken();
  
  let response = await fetch(`${API_BASE}/orders/upload`, {
    method: 'POST',
    headers: { 
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(order),
  });
  
  // Handle 401 - try token refresh and retry once
  if (response.status === 401) {
    const shouldRetry = await handleAuthError(response);
    if (shouldRetry) {
      const newToken = await getValidToken();
      response = await fetch(`${API_BASE}/orders/upload`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${newToken}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(order),
      });
    } else {
      throw new Error('Authentication failed. Please login again.');
    }
  }
  
  if (!response.ok) {
    throw new Error(`Order sync failed: ${response.statusText}`);
  }
  
  const result: APIResult<ClientSyncResult> = await response.json();
  return result;
}




export async function fetchClientOrderOptions(): Promise<APIResult<ClientOptionsResponse>> {
  const token = await getValidToken();
  
  let response = await fetch(`${API_BASE}/orders/options`, {
    headers: { 
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    method: 'GET',
    credentials: 'include',
  });
  
  // Handle 401 - try token refresh and retry once
  if (response.status === 401) {
    const shouldRetry = await handleAuthError(response);
    if (shouldRetry) {
      const newToken = await getValidToken();
      response = await fetch(`${API_BASE}/orders/options`, {
        headers: { 
          'Authorization': `Bearer ${newToken}`,
          'Content-Type': 'application/json'
        },
        method: 'GET',
        credentials: 'include',
      });
    } else {
      throw new Error('Authentication failed. Please login again.');
    }
  }
  
  if (!response.ok) {
    throw new Error(`Failed to fetch client options: ${response.statusText}`);
  }
  
  return await response.json();
}

/**
 * Delete an image on the server by its server ID
 * @param serverId The server image ID to delete
 */
export async function deleteImageOnServer(serverId: string): Promise<void> {
  const token = await getValidToken();
  
  let response = await fetch(`${API_BASE}/orders/delete/photo/${serverId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include',
  });
  
  // Handle 401 - try token refresh and retry once
  if (response.status === 401) {
    const shouldRetry = await handleAuthError(response);
    if (shouldRetry) {
      const newToken = await getValidToken();
      response = await fetch(`${API_BASE}/orders/delete/photo/${serverId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${newToken}` },
        credentials: 'include',
      });
    } else {
      throw new Error('Authentication failed. Please login again.');
    }
  }
  
  if (!response.ok) {
    throw new Error(`Failed to delete image on server: ${response.statusText}`);
  }
  
  console.log(`[deleteImageOnServer] Image ${serverId} deleted from server`);
}

/**
 * Download an image from the server by its server ID
 * @param serverId The server image ID to download
 * @returns Object with blob data, step, and tag
 */
export async function downloadImage(serverId: string): Promise<{ blob: Uint8Array; step: number; tag: string }> {
  const token = await getValidToken();
  
  let response = await fetch(`${API_BASE}/orders/photo/${serverId}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include',
  });
  
  // Handle 401 - try token refresh and retry once
  if (response.status === 401) {
    const shouldRetry = await handleAuthError(response);
    if (shouldRetry) {
      const newToken = await getValidToken();
      response = await fetch(`${API_BASE}/orders/photo/${serverId}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${newToken}` },
        credentials: 'include',
      });
    } else {
      throw new Error('Authentication failed. Please login again.');
    }
  }
  
  if (!response.ok) {
    throw new Error(`Failed to download image: ${response.statusText}`);
  }
  
  // Parse JSON response with base64 photo data
  const jsonData: { orderid: number; photo: string; step: number; tag: string } = await response.json();
  
  // Convert base64 string to Uint8Array
  const base64Data = jsonData.photo;
  const binaryString = atob(base64Data);
  const blob = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    blob[i] = binaryString.charCodeAt(i);
  }
  
  console.log(`[downloadImage] Image ${serverId} downloaded from server (${blob.length} bytes, step: ${jsonData.step}, tag: ${jsonData.tag})`);
  
  return {
    blob,
    step: jsonData.step,
    tag: jsonData.tag
  };
}
